import { XIcon } from "@heroicons/react/outline";
import { DownloadIcon } from "lucide-react";
import { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { downloadMedia } from "@/components/ui/MediaBlocks/utils";
import { cn } from "@/lib/utils";

export type MediaFullscreenDialogVariant = "dark" | "light";

export interface MediaFullscreenDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  modal?: boolean;
  preventOutsideDismiss?: boolean;
  downloadUrl?: string;
  downloadFilename?: string;
  variant?: MediaFullscreenDialogVariant;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "7xl" | "full";
  headerIcon?: ReactNode;
  headerSubtitle?: string;
  children: ReactNode;
}

const variantStyles = {
  dark: {
    dialog: "border-none bg-gray-900 shadow-2xl",
    header: "border-b border-gray-700 bg-gray-800",
    title: "text-white",
    subtitle: "text-gray-400",
    button: "text-gray-300 hover:bg-gray-700 hover:text-white",
    content: "bg-gray-900",
  },
  light: {
    dialog: "border-gray-200 bg-white shadow-2xl",
    header: "border-b border-gray-200 bg-gray-50",
    title: "text-gray-900",
    subtitle: "text-gray-500",
    button: "text-gray-500 hover:bg-gray-200 hover:text-gray-700",
    content: "bg-gray-100",
  },
};

const maxWidthClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
  full: "max-w-full",
};

export const MediaFullscreenDialog = ({
  isOpen,
  onClose,
  title,
  modal = true,
  preventOutsideDismiss = false,
  downloadUrl,
  downloadFilename,
  variant = "dark",
  maxWidth = "5xl",
  headerIcon,
  headerSubtitle,
  children,
}: MediaFullscreenDialogProps) => {
  const styles = variantStyles[variant];

  const handleDownload = () => {
    if (downloadUrl) {
      downloadMedia(downloadUrl, downloadFilename || title);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()} modal={modal}>
      <DialogContent
        className={cn(
          "flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0",
          maxWidthClasses[maxWidth],
          styles.dialog,
        )}
        shouldShowCloseButton={false}
        onPointerDownOutside={event => {
          if (preventOutsideDismiss) {
            event.preventDefault();
          }
        }}
        onInteractOutside={event => {
          if (preventOutsideDismiss) {
            event.preventDefault();
          }
        }}
      >
        {/* Header */}
        <div className={cn("flex items-center justify-between px-4 py-3", styles.header)}>
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {headerIcon}
            <div className="flex min-w-0 flex-col">
              <DialogTitle className={cn("truncate text-base font-semibold", styles.title)}>
                {title}
              </DialogTitle>
              {headerSubtitle && (
                <span className={cn("max-w-md truncate text-sm", styles.subtitle)}>
                  {headerSubtitle}
                </span>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {downloadUrl && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleDownload}
                className={cn("h-9 w-9", styles.button)}
                aria-label="Download"
              >
                <DownloadIcon className="h-4 w-4" />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onClose}
              className={cn("h-9 w-9", styles.button)}
              aria-label="Close"
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className={cn("flex flex-1 overflow-auto", styles.content)}>{children}</div>
      </DialogContent>
    </Dialog>
  );
};
